// 共通ログイン画面の純粋ロジック（external-design/portal.md「共通ログイン画面」）。
//
// OIDC ライブラリが送ってきた authorize 要求のパラメータを保持し、email のドメインで振り分け先を決め、
// 同じパラメータをそのまま Cognito の /oauth2/authorize へ転送する。

/** 転送する OAuth の標準パラメータ。これ以外は捨てる。 */
export const OAUTH_PARAMS = [
  "client_id", "redirect_uri", "response_type", "scope", "state",
  "code_challenge", "code_challenge_method", "nonce", "prompt",
] as const;

export interface AuthorizeRequest {
  params: URLSearchParams;
  loginHint?: string;
}

/** クエリから authorize 要求を取り出す。client_id と redirect_uri が無ければ要求ではない。 */
export function parseAuthorizeRequest(search: string): AuthorizeRequest | null {
  const q = new URLSearchParams(search);
  if (!q.get("client_id") || !q.get("redirect_uri")) return null;
  const params = new URLSearchParams();
  for (const k of OAUTH_PARAMS) {
    const v = q.get(k);
    if (v) params.set(k, v);
  }
  return { params, loginHint: q.get("login_hint") ?? undefined };
}

export interface IdpLookupResult {
  idpName: string | null;
  userPoolId: string | null;
  hostedUiDomain: string | null;
  clientId: string | null;
}

/** Managed Login の表示言語（lang）。Cognito がブラウザのクッキーに覚え、以後の画面も同じ言語になる。 */
export const MANAGED_LOGIN_LANG = "ja";

/**
 * Cognito の /oauth2/authorize の URL を組む。SSO なら identity_provider を足す。
 * hostedUiDomain が無ければ共有プールの既定ドメイン（config.json）。
 */
export function buildAuthorizeUrl(
  req: AuthorizeRequest,
  lookup: IdpLookupResult | null,
  defaultCognitoDomain: string,
): string {
  const domain = (lookup?.hostedUiDomain ?? defaultCognitoDomain).replace(/\/+$/, "");
  const p = new URLSearchParams(req.params);
  if (lookup?.idpName) p.set("identity_provider", lookup.idpName);
  p.set("lang", MANAGED_LOGIN_LANG);
  return `${domain}/oauth2/authorize?${p.toString()}`;
}

export const emailDomainOf = (email: string): string | null => {
  const at = email.lastIndexOf("@");
  return at > 0 && at < email.length - 1 ? email.slice(at + 1).toLowerCase() : null;
};

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** 契約 ID の形式（portal-api の CONTRACT_ID_RE と同じ。ADR-0029）。 */
export const CONTRACT_ID_RE = /^[a-z0-9][a-z0-9_]{0,47}$/;

/** 共通ログイン画面で最後に入力した email を覚えておく localStorage の鍵。 */
export const REMEMBER_KEY = "portal.lastEmail";

/**
 * 人が /login を直接開いたとき（authorize 要求なし）の login_hint。パスワードの招待メールのリンク
 * （/login?login_hint=<email>。ADR-0019）から来たとき、ポータル自身のログインに引き継いで入力を飛ばす。
 */
export function portalLoginHint(search: string): string | undefined {
  const v = new URLSearchParams(search).get("login_hint")?.trim().toLowerCase();
  return v && EMAIL_RE.test(v) ? v : undefined;
}

/** パスワードの招待メールのリンク（/login?login_hint=<email>&contract=<contractId>）が示す、招待した契約。 */
export function portalInvitedContract(search: string): string | undefined {
  const v = new URLSearchParams(search).get("contract")?.trim();
  return v && CONTRACT_ID_RE.test(v) ? v : undefined;
}

const normalizeEmail = (v: string | null | undefined): string | null => {
  const t = v?.trim().toLowerCase();
  return t && EMAIL_RE.test(t) ? t : null;
};

/**
 * メールアドレスの入力を飛ばして、そのまま振り分けてよいとき、振り分けに使う email。飛ばさないなら null。
 *
 * - ポータル自身の要求で login_hint があるとき（招待の受諾・紐付け直後のやり直し）
 * - サービスの要求で、同じタブでポータルにログイン中（ID トークンが期限内）のとき。その email で振り分ける
 *   （ADR-0021）。ポータルから遷移した人に同じ入力を求めないため。サービスが渡した login_hint がその email と
 *   違うときは飛ばさない（login_hint は既定値に留め、本人が別の email に直せるように）
 *
 * `portalSessionEmail` は同じタブのポータルのセッション（期限内の ID トークン）の email。無ければ null。
 */
export function emailToForward(
  req: AuthorizeRequest,
  portalClientId: string,
  portalSessionEmail: string | null | undefined,
): string | null {
  const hint = normalizeEmail(req.loginHint);
  if (req.params.get("client_id") === portalClientId) return hint;
  const session = normalizeEmail(portalSessionEmail);
  if (!session) return null;
  return hint === null || hint === session ? session : null;
}
